/*
 * ⚠️ 이 파일은 **두 벌 있다.**
 *     tastee_website/src/data/quiz.ts   (tastee.kr/quiz — 계정 없는 사람)
 *     tastee_web-main/src/data/quiz.ts  (앱 안 환경설정 > 취향분석)
 *
 * 문항이나 점수 계산을 고칠 때 **반드시 양쪽을 같이** 고친다. 한쪽만 고치면
 * 같은 테스트가 다른 결과를 내는데, 오류가 안 나서 아무도 모른다.
 * 저장소가 달라 공유할 방법이 마땅치 않아 이렇게 둔다.
 *
 * 보정값(quizCalibration.ts)은 웹사이트 쪽에서
 *     node scripts/calibrate-quiz.mjs
 * 로 만든 뒤 이쪽으로 복사한다. 점수를 고쳤으면 **반드시 다시 만든다.**
 *
 * 한 군데만 다르다 — import 경로의 '.ts' 확장자를 뗐다. 웹사이트는 Node
 * 스크립트가 직접 읽느라 붙여 두는데, 이쪽 빌드(CRA)는 그걸 거절한다.
 */
/**
 * 입맛 유형 테스트 v2 — 문항 데이터만 둔다 (화면·계산 코드 없음).
 *
 * - 문구나 점수를 고칠 때는 이 파일만 바꾼다.
 * - 점수를 바꾸면 `node scripts/calibrate-quiz.mjs`를 다시 돌려 보정값과 SCORING_VERSION을 갱신한다.
 * - 답 ID(a, b, c…)와 문항 ID(q1…)는 DB에 저장되므로 한번 배포한 뒤에는 바꾸지 않는다.
 *   문구만 바꾸는 건 괜찮다.
 * - 이 파일은 scripts/calibrate-quiz.mjs에서 Node로 직접 읽으므로 별칭 import(@/…)를 쓰지 않는다.
 */

export const TRAITS = [
  "explorer",
  "regular",
  "value",
  "experience",
  "solo",
  "mood",
  "trend",
  "waiting",
] as const;

export type Trait = (typeof TRAITS)[number];

export const TRAIT_LABEL: Record<Trait, string> = {
  explorer: "개척",
  regular: "단골",
  value: "가성비",
  experience: "경험",
  solo: "혼밥",
  mood: "분위기",
  trend: "트렌드",
  waiting: "검증",   // 화면에 보이는 이름. 성향 코드(waiting)는 그대로 둔다 — 서버·저장된 응답이 그 값을 쓴다
};

export type Points = Partial<Record<Trait, number>>;

export interface Answer {
  id: string;
  label: string;
  points?: Points;
  /** 인사이트 태그. key는 question.insightKey */
  insight?: string;
}

export interface FollowUp {
  id: string;
  question: string;
  insightKey: string;
  answers: Answer[];
}

export interface Question {
  id: string;
  question: string;
  /** 이 문항 답의 insight 값이 저장될 키 (없으면 저장 안 함) */
  insightKey?: string;
  answers: Answer[];
  /** 메인 답을 고르면 같은 화면 아래에 펼쳐지는 보조 질문 */
  followUp?: FollowUp;
}

export const QUESTIONS: Question[] = [
  {
    id: "q1",
    question: '단톡방에 "오늘 뭐 먹지?"가 올라왔다. 너의 반응은?',
    insightKey: "role",
    answers: [
      { id: "a", label: "1분 만에 안 가본 3곳 링크 투척", points: { explorer: 1 }, insight: "place_lead" },
      { id: "b", label: '"거기 가자, 우리 맨날 가던 데"', points: { regular: 2 }, insight: "place_lead" },
      { id: "c", label: '"오늘은 무조건 고기!" 메뉴만 던지고 장소는 맡김', insight: "menu_lead" },
      { id: "d", label: '"난 다 좋아~ 정해지면 알려줘" (진심)', insight: "follower" },
    ],
  },
  {
    id: "q2",
    question: "이 맛집 무조건 믿고 간다! 뭘 보고?",
    insightKey: "trust",
    answers: [
      { id: "a", label: "지도 앱 별점 4.5 이상", insight: "map_rating" },
      { id: "b", label: "TV에 나온 집", points: { trend: 1 }, insight: "tv" },
      { id: "c", label: "유명 맛집 인플루언서가 소개한 집", points: { trend: 1 }, insight: "influencer" },
      { id: "d", label: "친구가 맛있다고 한 집", points: { regular: 1 }, insight: "friend" },
      { id: "e", label: "매장 앞 웨이팅, 이게 최고의 리뷰", points: { waiting: 1 }, insight: "queue" },
    ],
    followUp: {
      id: "q2f",
      question: "그렇게 믿고 간 곳, 기대보다 별로였던 적은?",
      insightKey: "disappointment",
      answers: [
        { id: "a", label: "자주 있다", insight: "often" },
        { id: "b", label: "가끔 있다", insight: "sometimes" },
        { id: "c", label: "거의 없다", insight: "rarely" },
      ],
    },
  },
  {
    id: "q3",
    question: "외식할 때 내 옆자리 고정 멤버는?",
    insightKey: "companion",
    answers: [
      { id: "a", label: "나 혼자가 제일 편해, 메뉴도 속도도 내 맘대로", points: { solo: 2 }, insight: "alone" },
      { id: "b", label: "친구들이랑, 오늘은 배 터질 때까지", insight: "friends" },
      { id: "c", label: "애인이랑, 새로운 맛집 도장깨기 중", points: { explorer: 1 }, insight: "partner" },
      { id: "d", label: "가족, 오늘은 아빠 카드 찬스", insight: "family" },
      { id: "e", label: "외식? 집에서 먹자 주의", insight: "homebody" },
    ],
  },
  {
    id: "q4",
    question: '단골집에 "신메뉴 출시!" 포스터가 붙었다',
    insightKey: "new_menu",
    answers: [
      { id: "a", label: "무조건 신메뉴, 첫 리뷰는 내 거", points: { trend: 1, explorer: 1 }, insight: "high" },
      { id: "b", label: "남들 반응 좀 보고 다음에", points: { waiting: 1 }, insight: "mid" },
      { id: "c", label: "늘 먹던 거, 그게 여기 온 이유", points: { regular: 2 }, insight: "low" },
      { id: "d", label: "신메뉴 + 원래 메뉴 둘 다 시킴", points: { experience: 1 }, insight: "high" },
    ],
  },
  {
    id: "q5",
    question: "음식이 나왔는데, 생각보다 양이 적다",
    answers: [
      { id: "a", label: "이 가격에 이 양? 다음엔 안 와", points: { value: 2 } },
      { id: "b", label: "괜찮아, 떡볶이로 2차 가면 돼", points: { value: 1 } },
      { id: "c", label: "양은 적어도 분위기 좋으면 인정", points: { mood: 1 } },
      { id: "d", label: "양보다 맛의 완성도, 한 접시라도 제대로면 OK", points: { experience: 2 } },
    ],
  },
  {
    id: "q6",
    question: "이번 주 고생한 나에게 주는 보상, 오늘은 고기다. 어디로 갈까?",
    insightKey: "budget",
    answers: [
      { id: "a", label: "1인분 9,900원 냉동 삼겹살집, 지갑은 가볍게 배는 든든하게", points: { value: 1 }, insight: "under_15k" },
      { id: "b", label: "동네 가성비 고깃집, 보상은 소소하게 확실하게", points: { regular: 1, value: 1 }, insight: "15k_30k" },
      { id: "c", label: "숙성 한돈·이베리코 전문점, 그릴링 서비스는 덤", points: { experience: 1 }, insight: "30k_50k" },
      { id: "d", label: "한우 오마카세, 오늘은 나를 위한 날", points: { experience: 2 }, insight: "over_50k" },
    ],
  },
  {
    id: "q7",
    question: "친구가 맛집을 추천해 준대. 어떤 한마디에 바로 가?",
    insightKey: "persuasion",
    answers: [
      { id: "a", label: '"셰프가 파인다이닝 출신이래"', points: { experience: 1 }, insight: "chef" },
      { id: "b", label: '"가게 들어가는 순간 분위기가 달라"', points: { mood: 1 }, insight: "space" },
      { id: "c", label: '"오픈 전부터 줄 서는 집이야"', points: { waiting: 1 }, insight: "popularity" },
      { id: "d", label: '"만오천 원이면 배 터지게 먹어"', points: { value: 1 }, insight: "price" },
    ],
  },
  {
    id: "q8",
    question: "핫하다는 식당, 웨이팅 1시간이래",
    answers: [
      { id: "a", label: "1시간? 직접 먹어봐야 알지, 줄 선다", points: { waiting: 1, trend: 1 } },
      { id: "b", label: "기다리는 동안 외관 사진이나 찍어야지", points: { waiting: 1, mood: 2 } },
      { id: "c", label: "근처 아는 집 간다, 시간 아까워", points: { regular: 1 } },
      { id: "d", label: "나만 아는 골목집으로 조용히 이동", points: { explorer: 1, solo: 1 } },
    ],
  },
  {
    id: "q9",
    question: "요즘 피드에 같은 디저트가 계속 뜬다. 너는?",
    insightKey: "trend_adoption",
    answers: [
      { id: "a", label: "남들 알기 전에 이미 먹어봄, 원조는 나", points: { trend: 1, explorer: 1 }, insight: "pioneer" },
      { id: "b", label: "이번 주 안에 먹어 본다, 유행은 타이밍이지", points: { trend: 2 }, insight: "early" },
      { id: "c", label: "줄 좀 줄고 후기 쌓이면 그때", points: { waiting: 1 }, insight: "after_proof" },
      { id: "d", label: "유행은 유행일 뿐, 관심 없음", points: { trend: -1 }, insight: "indifferent" },
    ],
  },
  {
    id: "q10",
    question: "처음 가는 식당, 들어가자마자 보는 것은?",
    answers: [
      { id: "a", label: "조명, 음악, 공간 분위기부터", points: { mood: 2 } },
      { id: "b", label: "메뉴판 가격부터", points: { value: 1 } },
      { id: "c", label: "벽에 붙은 연예인 사인, 방송 출연 액자부터", points: { trend: 1 } },
      { id: "d", label: "혼자 앉기 편한 자리 있나", points: { solo: 2 } },
    ],
  },
  {
    id: "q11",
    question: "당신의 혼밥 레벨은?",
    answers: [
      { id: "a", label: "고깃집 혼자 2인분 가능한 혼밥 마스터", points: { solo: 2 } },
      { id: "b", label: "국밥·라멘·패스트푸드 정도는 혼자 OK", points: { solo: 1, value: 1 } },
      { id: "c", label: "카페는 혼자 OK, 식당은 좀…" },
      { id: "d", label: "혼자 먹을 바엔 굶는다", points: { solo: -1 } },
    ],
  },
  {
    id: "q12",
    question: "평일 저녁, 동네에서 밥 먹을 곳 고를 때 너는?",
    answers: [
      { id: "a", label: "지나가다 봐둔 새로 생긴 집, 오늘 들어가 본다", points: { explorer: 2 } },
      { id: "b", label: "동네에서 제일 유명한 집, 줄 좀 서도 실패는 싫어", points: { waiting: 2 } },
      { id: "c", label: '저장해둔 "가보고 싶은 곳" 리스트에서 하나 꺼낸다', points: { explorer: 1, experience: 1 } },
      { id: "d", label: "결국 늘 가던 그 집, 메뉴판 안 봐도 주문 가능", points: { regular: 2 } },
    ],
  },
  {
    id: "q13",
    question: "진짜 괜찮은 맛집을 찾았다! 너는?",
    insightKey: "sharing",
    answers: [
      { id: "a", label: '다음 약속 때 "나 맛집 아는데" 하고 데려간다', points: { regular: 1 }, insight: "bring_friends" },
      { id: "b", label: "인스타 스토리에 위치 태그", points: { trend: 1 }, insight: "sns" },
      { id: "c", label: "찐친한테만 살짝, 유명해지면 안 돼", points: { regular: 1 }, insight: "hideout" },
      { id: "d", label: "아무한테도 말 안 하고 즐겨찾기에 저장", points: { solo: 1 }, insight: "none" },
    ],
    followUp: {
      id: "q13f",
      question: "그 맛집, 어디에 남겨 둬?",
      insightKey: "saving_habit",
      answers: [
        { id: "a", label: "지도 앱에 카테고리별로 정리", insight: "organized" },
        { id: "b", label: "일단 저장만", insight: "save_only" },
        { id: "c", label: "사진첩에만", insight: "photos" },
        { id: "d", label: "안 남김, 기억하면 됨", insight: "memory" },
      ],
    },
  },
  {
    id: "q14",
    question: "기대했던 식당이 별로였다. 너의 반응은?",
    insightKey: "complaint",
    answers: [
      { id: "a", label: "바로 리뷰, 뭐가 별로였는지 가격·양까지 조목조목", points: { value: 1 }, insight: "detailed_review" },
      { id: "b", label: "리뷰는 귀찮고 별점만 낮게", insight: "rating_only" },
      { id: "c", label: '친구들한테만 "거기 가지 마"', points: { regular: 1 }, insight: "friends_only" },
      { id: "d", label: "아무 말 없이 조용히, 다신 안 감", insight: "silent_exit" },
    ],
  },
];

/** 마지막 칩 (점수 없음) */
export const CHIP: FollowUp = {
  id: "chip_freq",
  question: "한 달에 외식(배달 제외) 몇 번 정도 해요?",
  insightKey: "dining_frequency",
  answers: [
    { id: "a", label: "월 4회 미만", insight: "under_4_month" },
    { id: "b", label: "주 1~2회", insight: "1_2_week" },
    { id: "c", label: "주 3~4회", insight: "3_4_week" },
    { id: "d", label: "거의 매일", insight: "daily" },
  ],
};

/** 같은 동점일 때 쓰는 고정 순서 */
export const TIE_ORDER: Trait[] = [...TRAITS];
